/** @format */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup } from '@material-ui/core';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import I18n from '../../../components/Translate/I18n';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { useDispatch } from 'react-redux';
import CustomInputExt from '../../../components/CustomInput/CustomInputExt';
import { SimpleSelect } from '../../../components/Adescom/SimpleSelect';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral';
import Filter from '../../../interfaces/forms/Filter';
import { usePlatformResellerHeaders, usePlatformResellerList } from '../../../modules/lib/hooks/usePlatformResellerHooks';
import {
    apiShortPlatformIpRange,
    PlatformIpRange,
    PlatformIpRangeResponse,
    PlatformIpRangeResponseCollectionInterface,
} from '../../../interfaces/platform/PlatformIpPoolNumber';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import { configDefault } from '../../../core/config';
import { ApiCollectionInterface } from '../../../interfaces/collections/ApiCollectionInterface';
import { ApnNameInterface } from '../../../interfaces/collections/ApnNameInterface';
import { HeaderCheckBoxSelectListElement } from '../../lib/search/listTableHeader/HeaderCheckBoxSelectList';
import i18n from '../../../core/i18n';

type IpChangeOwnerType = {
    open: boolean;
    reload(): void;
    close(): void;
};

export const IpChangeOwner: FunctionComponent<IpChangeOwnerType> = ({ open, reload, close }): React.ReactElement => {
    const [platformResellerId, setPlatformResellerId] = useState<number | null>(null);
    const [apnNameId, setApnNameId] = useState<number | null>(null);
    const [selectedValue, setSelectedValue] = useState(false);
    const [fromIp, setFromIp] = useState<string | null>(null);
    const [toIp, setToIp] = useState<string | null>(null);
    const [totalIp, setTotalIp] = useState(0);
    const dispatch = useDispatch();
    const [publicApnName, setPublicApnName] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [privateApnName, setPrivateApnName] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [apnName, setApnName] = useState<Array<HeaderCheckBoxSelectListElement> | null>(null);
    const [disableSecurity, setDisableSecurity] = useState(false);

    const platformResellerList = usePlatformResellerList();
    const platformResellerHeader = usePlatformResellerHeaders(platformResellerList);

    useEffect(() => {
        if (((fromIp && fromIp.length > 0) || (toIp && toIp.length > 0)) && apnNameId && platformResellerId) {
            const itemLoader = new ItemLoaders(dispatch);
            const filters: Array<Filter> = [
                { name: 'apnNameId', value: apnNameId },
                { name: 'platformResellerId', value: platformResellerId },
                { name: 'disableSecurity', value: disableSecurity },
            ];

            if (fromIp && fromIp.length > 0) {
                filters.push({ name: 'fromIp', value: fromIp });
            }
            if (toIp && toIp.length > 0) {
                filters.push({ name: 'toIp', value: toIp });
            }
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/' + apiShortPlatformIpRange)),
                'none',
                filters,
                [],
                (data: PlatformIpRangeResponseCollectionInterface): void => {
                    setTotalIp(data['hydra:member'][0].rangeCount);
                    setSelectedValue(data['hydra:member'][0].rangeCount > 0);
                },
                { propagateDataToCallback: true, dispatchAfterCallback: false }
            );
        }
    }, [dispatch, fromIp, toIp, apnNameId, platformResellerId, disableSecurity]);

    useEffect(() => {
        if (platformResellerId && publicApnName === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/apn_names', configDefault.PLATFORM_API_PREFIX)),
                'platform-reseller-apn-name-public-list',
                [
                    { name: 'isDeleted', value: false },
                    { name: 'isPublic', value: true },
                ],
                [{ name: 'apnName', value: 'asc' }],
                (data: ApiCollectionInterface<ApnNameInterface>): void => {
                    const tmp: Array<HeaderCheckBoxSelectListElement> = [];
                    data['hydra:member'].map((data): void => {
                        tmp.push({ value: data.id, name: data.apnName + i18n.t('pools:apn.public') });
                    });
                    setPublicApnName(tmp);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [platformResellerId, dispatch, publicApnName]);

    useEffect(() => {
        if (publicApnName && privateApnName && apnName === null) {
            setApnName([...publicApnName, ...privateApnName]);
        }
    }, [publicApnName, privateApnName, apnName]);

    useEffect(() => {
        if (platformResellerId && privateApnName === null) {
            const itemLoader = new ItemLoaders(dispatch);
            itemLoader.getListWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/apn_names', configDefault.PLATFORM_API_PREFIX)),
                'platform-reseller-apn-name-public-list',
                [
                    { name: 'isDeleted', value: false },
                    { name: 'isPublic', value: false },
                    { name: 'platformReseller', value: platformResellerId },
                ],
                [{ name: 'apnName', value: 'asc' }],
                (data: ApiCollectionInterface<ApnNameInterface>): void => {
                    const tmp: Array<HeaderCheckBoxSelectListElement> = [];
                    data['hydra:member'].map((data): void => {
                        tmp.push({ value: data.id, name: data.apnName + i18n.t('pools:apn.private') });
                    });
                    setPrivateApnName(tmp);
                },
                { dispatchAfterCallback: false, propagateDataToCallback: true }
            );
        }
    }, [platformResellerId, dispatch, privateApnName]);

    const onChangeEvent = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        const filedName = event.target.name;
        if (filedName === 'platformResellerId') {
            setPlatformResellerId(parseInt(event.target.value));
            setPrivateApnName(null);
            setApnName(null);
            setApnNameId(null);
            setSelectedValue(false);
        }
        if (filedName === 'apnNameId') {
            setApnNameId(parseInt(event.target.value));
        }
    };

    const validateIp = (ip: string): boolean => {
        const regexExp = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/gi;
        return regexExp.test(ip);
    };

    const confirmCallback = (): void => {
        if (!fromIp || !validateIp(fromIp)) {
            NotificationHelper.notifyError('pools:sim.range_from_ip_empty_from_number', dispatch);
            return;
        }
        if (!toIp || !validateIp(toIp)) {
            NotificationHelper.notifyError('pools:sim.range_from_ip_empty_to_number', dispatch);
            return;
        }
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.putWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/' + apiShortPlatformIpRange + '/-1')),
            'change_owner_call_api',
            {
                apnNameId: apnNameId,
                fromIp: fromIp,
                toIp: toIp ?? fromIp,
                ownerPlatformResellerId: platformResellerId,
                disableSecurity: disableSecurity,
            } as PlatformIpRange,
            (data: PlatformIpRangeResponse) => {
                reload();
                close();
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const updateCheckBox = (name: string, value: boolean): void => {
        setDisableSecurity(value);
    };

    return (
        <Dialog open={open} onClose={close} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
            <DialogTitle id="alert-dialog-title">
                <I18n trans="pools:sim.owner_change" />
            </DialogTitle>
            <DialogContent>
                <GridContainer>
                    <GridItem xs={6}>
                        <CustomInputExt
                            labelText="pools:sim.range_from_ip"
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setFromIp(event.currentTarget.value);
                                setSelectedValue(false);
                            }}
                            value={fromIp}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <CustomInputExt
                            labelText="pools:sim.range_to_ip"
                            onChange={(event: React.FormEvent<HTMLInputElement>): void => {
                                setToIp(event.currentTarget.value);
                                setSelectedValue(false);
                            }}
                            value={toIp}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <SimpleSelect
                            dispatchName={'none'}
                            label="pools:sim.choose_partner"
                            fieldName="platformResellerId"
                            selectedValue={platformResellerId ?? undefined}
                            required={true}
                            callback={onChangeEvent}
                            elements={platformResellerHeader}
                        />
                    </GridItem>
                    <GridItem xs={6}>
                        <SimpleSelect
                            dispatchName={'none'}
                            label="pools:apn.apn_name"
                            fieldName="apnNameId"
                            selectedValue={apnNameId ?? undefined}
                            required={true}
                            callback={onChangeEvent}
                            elements={apnName ?? []}
                        />
                    </GridItem>
                    <GridItem xs={12}>
                        <FormGroup aria-label="position" row>
                            <FormControlLabel
                                value="disableSecurity"
                                control={
                                    <Checkbox
                                        checked={disableSecurity}
                                        onChange={(event, checked): void => {
                                            updateCheckBox('disableSecurity', checked);
                                        }}
                                    />
                                }
                                label={<I18n trans="pools:apn.remove_assign" />}
                                labelPlacement="start"
                            />
                        </FormGroup>
                    </GridItem>
                    <GridItem xs={12}>
                        <div style={{ marginTop: '20px' }}>
                            <I18n trans="pools:sim.total_numbers" valueObject={{ count: totalIp }} />
                        </div>
                    </GridItem>
                </GridContainer>
            </DialogContent>
            <DialogActions>
                <CustomTextButtom callback={close} callbackParam={{}} label="general:common.close" size="sm" />
                {selectedValue && fromIp && (
                    <CustomTextButtom callback={confirmCallback} callbackParam={{}} label="pools:sim.confirm_partner_change" size="sm" />
                )}
            </DialogActions>
        </Dialog>
    );
};
